import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { routes } from "consts";
import { AdminResourceDeleteModal, AdminSearchInput, PageMain } from "components/molecules";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { TanTable, useFilters, usePagination, useSorting, TanTableColumn } from "components/molecules";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { useLandingPages, LandingPageApi, shopApi } from "api/shop";
import { useDeleteResourceActions } from "utils/store";
import { useToaster } from "utils";
import { PaziaBox, PaziaSideNav, PaziaBadge } from "components/atoms";
import { ThemeColors } from "consts/theme";
import { ShopSettingsForm } from "./Forms/ShopSettingsForm";
import { ShopFooterForm } from "./Forms/ShopFooterForm";
import { ShopGoogleAnalyticsForm } from "./Forms/ShopGoogleAnalyticsForm";
import { ShopMetaPixelForm } from "./Forms/ShopMetaPixelForm";
import { ShopButtonStylesForm } from "./Forms/ShopButtonStylesForm";
import { ShopHeadingStylesForm } from "./Forms/ShopHeadingStylesForm";
import { ShopMenuForm } from "./Forms/ShopMenuForm";
import { ShopProductCardForm } from "./Forms/ShopProductCardForm";
import { ShopCourseDetailPageForm } from "./Forms/ShopCourseDetailPageForm";
import { ShopBreadcrumbsForm } from "./Forms/ShopBreadcrumbsForm";
import { ShopArchivedCoursePageSettingsForm } from "./Forms/ShopArchivedCoursePageSettingsForm";
import { ShopCourseGroupSettingsForm } from "./Forms/ShopCourseGroupSettingsForm";
import { ShopRedirectsPage } from "./Redirects/ShopRedirectsPage";
import { ShopSeoPage } from "./Forms/ShopSeoPage";
import { ShopSeoSettingsForm } from "./Forms/ShopSeoSettingsForm";
import { ShopContactFormSettingsForm } from "./Forms/ShopContactFormSettingsForm";
import { ContactFormSubmissionsPage } from "./ContactFormSubmissions/ContactFormSubmissionsPage";

export const LandingPagesPage = () => {
  const [activeTab, setActiveTab] = useState<
    | "pages"
    | "settings"
    | "footer"
    | "buttons"
    | "heading-styles"
    | "menu"
    | "product-card"
    | "course-detail"
    | "breadcrumbs"
    | "redirects"
    | "seo"
    | "analytics"
    | "archived-course"
    | "course-groups"
    | "contact-form-settings"
    | "contact-form-submissions"
  >("pages");

  const navItems = [
    { name: "Strony", active: activeTab === "pages", onClick: () => setActiveTab("pages") },
    { name: "Menu", active: activeTab === "menu", onClick: () => setActiveTab("menu") },
    { name: "Ustawienia", active: activeTab === "settings", onClick: () => setActiveTab("settings") },
    { name: "Footer", active: activeTab === "footer", onClick: () => setActiveTab("footer") },
    { name: "Przyciski", active: activeTab === "buttons", onClick: () => setActiveTab("buttons") },
    { name: "Styl nagłówków", active: activeTab === "heading-styles", onClick: () => setActiveTab("heading-styles") },
    { name: "Karta produktu", active: activeTab === "product-card", onClick: () => setActiveTab("product-card") },
    { name: "Strona kursu", active: activeTab === "course-detail", onClick: () => setActiveTab("course-detail") },
    { name: "Breadcrumby", active: activeTab === "breadcrumbs", onClick: () => setActiveTab("breadcrumbs") },
    { name: "Kurs archiwalny", active: activeTab === "archived-course", onClick: () => setActiveTab("archived-course") },
    { name: "Grupy kursów", active: activeTab === "course-groups", onClick: () => setActiveTab("course-groups") },
    { name: "Przekierowania", active: activeTab === "redirects", onClick: () => setActiveTab("redirects") },
    {
      name: "Ustawienia formularza kontaktowego",
      active: activeTab === "contact-form-settings",
      onClick: () => setActiveTab("contact-form-settings"),
    },
    {
      name: "Zgłoszenia z formularza kontaktowego",
      active: activeTab === "contact-form-submissions",
      onClick: () => setActiveTab("contact-form-submissions"),
    },
    { name: "SEO", active: activeTab === "seo", onClick: () => setActiveTab("seo") },
    { name: "Analityka", active: activeTab === "analytics", onClick: () => setActiveTab("analytics") },
  ];

  return (
    <PageMain>
      <PageRichHeader header="Sklep">
        <></>
      </PageRichHeader>
      <PaziaBox>
        <div className="flex gap-8">
          <PaziaSideNav items={navItems} />
          <div className="min-w-0 flex-1">
            {activeTab === "pages" && <LandingPagesTab />}
            {activeTab === "menu" && <ShopMenuForm />}
            {activeTab === "settings" && <ShopSettingsForm />}
            {activeTab === "footer" && <ShopFooterForm />}
            {activeTab === "buttons" && <ShopButtonStylesForm />}
            {activeTab === "heading-styles" && <ShopHeadingStylesForm />}
            {activeTab === "product-card" && <ShopProductCardForm />}
            {activeTab === "course-detail" && <ShopCourseDetailPageForm />}
            {activeTab === "breadcrumbs" && <ShopBreadcrumbsForm />}
            {activeTab === "archived-course" && <ShopArchivedCoursePageSettingsForm />}
            {activeTab === "course-groups" && <ShopCourseGroupSettingsForm />}
            {activeTab === "redirects" && <ShopRedirectsPage embedded />}
            {activeTab === "contact-form-settings" && <ShopContactFormSettingsForm />}
            {activeTab === "contact-form-submissions" && <ContactFormSubmissionsPage embedded />}
            {activeTab === "seo" && (
              <>
                <ShopSeoSettingsForm />
                <div className="mt-8" />
                <ShopSeoPage />
              </>
            )}
            {activeTab === "analytics" && (
              <>
                <ShopGoogleAnalyticsForm />
                <div className="mt-8" />
                <ShopMetaPixelForm />
              </>
            )}
          </div>
        </div>
      </PaziaBox>
    </PageMain>
  );
};

const LandingPagesTab = () => {
  const { setDeleteResource } = useDeleteResourceActions();
  const { searchPhrase, setSearchPhrase } = useFilters();
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();

  const { isLoading, isFetching, landingPages, total, invalidate } = useLandingPages(skip, limit, searchPhrase);

  const onDuplicate = async (row: LandingPageApi) => {
    try {
      await shopApi.duplicateLandingPage(row.id);
      showSuccess("Zduplikowano stronę");
    } catch (error) {
      showError("Nie udało się zduplikować strony");
    } finally {
      invalidate();
    }
  };

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase]);

  const columns = React.useMemo<TanTableColumn<LandingPageApi>[]>(() => cols, []);

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <AdminSearchInput onSearch={(searchPhrase: string) => setSearchPhrase(searchPhrase)} className="self-end" />
        <PaziaButton type="primary" title="Dodaj stronę +" onClick={() => navigate(routes.SHOP_LANDING_PAGES_ADD)} />
      </div>

      <TanTable
        data={landingPages}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        onDelete={(x) => setDeleteResource(x.id)}
        onDuplicate={onDuplicate}
        pagination={pagination}
        onEdit={(x) => navigate(routes.SHOP_LANDING_PAGES_EDIT(x.id))}
        sorting={sorting}
        onRowClick={(x) => navigate(routes.SHOP_LANDING_PAGES_EDIT(x.id))}
        hideBorder
      />
      <AdminResourceDeleteModal
        title="Usuń stronę"
        description="Czy na pewno chcesz usunąć tę stronę? Wszystkie sekcje zostaną również usunięte."
        onSuccessDelete={() => invalidate()}
        deleteAction={shopApi.deleteLandingPage}
      />
    </>
  );
};

const cols: TanTableColumn<LandingPageApi>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: false,
    size: 60,
    cell: (info) => info.getValue(),
  },
  {
    id: "name",
    accessorKey: "name",
    header: "Nazwa",
    size: 300,
    enableColumnFilter: false,
  },
  {
    id: "slug",
    accessorKey: "slug",
    header: "Slug",
    size: 200,
    enableColumnFilter: false,
    cell: (info) => (
      <code className="rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-700">/{info.getValue<string>()}</code>
    ),
  },
  {
    id: "isPublished",
    accessorKey: "isPublished",
    header: "Status",
    size: 120,
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue<boolean>() ? (
        <PaziaBadge color={"emerald" as ThemeColors}>Opublikowana</PaziaBadge>
      ) : (
        <PaziaBadge color={"slate" as ThemeColors}>Szkic</PaziaBadge>
      ),
  },
  {
    id: "createdDate",
    accessorKey: "createdDate",
    header: "Utworzono",
    cell: (info) => info.getValue<Date>()?.toLocaleDateString("pl-PL"),
    size: 150,
    enableColumnFilter: false,
  },
];
